import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import ClinicianPatientWrapper from 'ui/clinicianScreens/Patient/ClinicianPatientWrapper'
import EditPatient from 'ui/screens/EditPatient'
import Root from 'ui/screens/Root'
import { ROUTES } from './consts'
import {
  ProtectedByAdmin,
  ProtectedByAdminOrPHI,
  ProtectedByFeatureFlag,
  ProtectedByLogin,
  ProtectedByPortalExperience,
} from './ProtectedRoutes'
import { useTitle } from 'ui/hooks'
import ProtectedBySuperAdmin from 'app/AppRoutes/ProtectedRoutes/ProtectedBySuperAdmin'
import { FLAGS } from 'domains/launchdarkly/flags'

/**
 * Patient tabs whose routes are only reachable inside the viewer's portal
 * experience. `tabKey` is the preset membership key; `routeKeys` are the
 * `ROUTES` entries that key unlocks, so one tab can own more than one route.
 */
const PORTAL_EXPERIENCE_ROUTES = [
  { tabKey: 'summary', routeKeys: ['summary', 'summaryComparison'] },
  { tabKey: 'weeklyView', routeKeys: ['weeklyView'] },
  { tabKey: 'daily', routeKeys: ['daily'] },
  { tabKey: 'log', routeKeys: ['log'] },
  { tabKey: 'reportPatient', routeKeys: ['reportPatient'] },
  { tabKey: 'reportClinician', routeKeys: ['reportClinician'] },
]

const AppRoutes = () => {
  const { pathname } = useLocation()
  useTitle(pathname)
  // Reset scrolling position when path changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return (
    <Routes>
      <Route element={<ProtectedByLogin />}>
        <Route path="/" element={<Root />}>
          <Route
            index
            element={<Navigate to={ROUTES.patients.path} replace />}
          />

          {/* --------PATIENTS------- */}
          <Route path={ROUTES.patients.path}>
            {/* --------DEFAULT (NO MATCH)------- */}
            <Route path="*" element={<Navigate to={ROUTES.patients.path} />} />

            {/* --------PATIENT LIST------- */}
            <Route index element={ROUTES.patients.element} />

            {/* --------TASK NOTIFICATIONS------- */}
            <Route element={<ProtectedByAdmin redirectPath=".." />}>
              <Route
                path={ROUTES.taskNotifications.path}
                element={ROUTES.taskNotifications.element}
              />
            </Route>

            {/* --------SINGLE PATIENT------- */}
            <Route
              path={ROUTES.patientId.path}
              element={ROUTES.patientId.element}
            >
              <Route
                path={ROUTES.reportPatientPDFTest.path}
                element={ROUTES.reportPatientPDFTest.element}
              />
              <Route element={<ClinicianPatientWrapper />}>
                {/* --------DEFAULT (NO MATCH)------- */}
                <Route
                  index
                  path="*"
                  element={<Navigate to={ROUTES.overview.path} replace />}
                />

                {/* --------MATCHED ROUTES------- */}
                <Route
                  path={ROUTES.overview.path}
                  element={ROUTES.overview.element}
                />
                {PORTAL_EXPERIENCE_ROUTES.map(({ tabKey, routeKeys }) => (
                  <Route
                    key={tabKey}
                    element={
                      <ProtectedByPortalExperience
                        tabKey={tabKey}
                        redirectPath={ROUTES.overview.path}
                      />
                    }
                  >
                    {routeKeys.map((routeKey) => (
                      <Route
                        key={routeKey}
                        path={ROUTES[routeKey].path}
                        element={ROUTES[routeKey].element}
                      />
                    ))}
                  </Route>
                ))}
                {/* --------PATIENT SETTINGS------- */}
                <Route element={<ProtectedByAdminOrPHI redirectPath=".." />}>
                  <Route
                    path={ROUTES.editPatient.path}
                    // element={ROUTES.editPatient.element}  // doesn't work
                    element={<EditPatient />} // works
                  >
                    {/* --------DEFAULT (NO MATCH)------- */}
                    <Route
                      index
                      path="*"
                      element={<Navigate to={ROUTES.details.path} replace />}
                    />

                    {/* --------MATCHED ROUTES------- */}
                    <Route
                      path={ROUTES.details.path}
                      element={ROUTES.details.element}
                    />
                    <Route
                      path={ROUTES.clients.path}
                      element={ROUTES.clients.element}
                    />
                    <Route path={ROUTES.devices.path}>
                      <Route index element={ROUTES.devices.element} />
                      <Route
                        path={ROUTES.deviceId.path}
                        element={ROUTES.deviceId.element}
                      />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>

          {/* --------USER SETTINGS------- */}
          <Route path={ROUTES.settings.path} element={ROUTES.settings.element}>
            {/* --------DEFAULT (NO MATCH)------- */}
            <Route
              index
              path="*"
              element={<Navigate to={ROUTES.profile.path} replace />}
            />

            {/* --------MATCHED ROUTES------- */}
            <Route
              path={ROUTES.profile.path}
              element={ROUTES.profile.element}
            />
            <Route
              path={ROUTES.cookiePreferences.path}
              element={ROUTES.cookiePreferences.element}
            />
            <Route
              path={ROUTES.accessTokens.path}
              element={ROUTES.accessTokens.element}
            />
          </Route>

          {/* --------USERS------- */}
          <Route
            element={<ProtectedByAdmin redirectPath={ROUTES.patients.path} />}
          >
            <Route path={ROUTES.users.path} element={ROUTES.users.element} />
          </Route>

          {/* --------ORGANIZATIONS------- */}
          <Route
            element={
              <ProtectedBySuperAdmin redirectPath={ROUTES.patients.path} />
            }
          >
            <Route
              path={ROUTES.organizations.path}
              element={ROUTES.organizations.element}
            />
          </Route>

          {/* --------ADMIN------- */}
          <Route
            element={
              <ProtectedBySuperAdmin redirectPath={ROUTES.patients.path} />
            }
          >
            <Route path={ROUTES.admin.path} element={ROUTES.admin.element}>
              <Route
                index
                element={<Navigate to="outreach/dashboard" replace />}
              />
              <Route
                path={ROUTES.adminOutreachDashboard.path}
                element={ROUTES.adminOutreachDashboard.element}
              />
              <Route
                path={ROUTES.adminOutreachTags.path}
                element={ROUTES.adminOutreachTags.element}
              />
              <Route
                path={ROUTES.adminOutreachTemplates.path}
                element={ROUTES.adminOutreachTemplates.element}
              />
              <Route
                path={ROUTES.adminOutreachCampaigns.path}
                element={ROUTES.adminOutreachCampaigns.element}
              />
              <Route
                path={ROUTES.adminOutreachHighlightCards.path}
                element={ROUTES.adminOutreachHighlightCards.element}
              />
              <Route
                path={ROUTES.adminOutreachHighlightCardDetails.path}
                element={ROUTES.adminOutreachHighlightCardDetails.element}
              />
              <Route
                path={ROUTES.adminOutreachCardContentEditor.path}
                element={ROUTES.adminOutreachCardContentEditor.element}
              />
              <Route
                path={ROUTES.adminOutreachPushNotifications.path}
                element={ROUTES.adminOutreachPushNotifications.element}
              />
              <Route
                path={ROUTES.adminOutreachCampaignDashboard.path}
                element={ROUTES.adminOutreachCampaignDashboard.element}
              />
              <Route
                path={ROUTES.adminOutreachCampaignEdit.path}
                element={ROUTES.adminOutreachCampaignEdit.element}
              />
              <Route
                path={ROUTES.adminOutreachTagNew.path}
                element={ROUTES.adminOutreachTagNew.element}
              />
              <Route
                path={ROUTES.adminOutreachTagEdit.path}
                element={ROUTES.adminOutreachTagEdit.element}
              />
              <Route
                path={ROUTES.adminOutreachTemplateNew.path}
                element={ROUTES.adminOutreachTemplateNew.element}
              />
              <Route
                path={ROUTES.adminOutreachTemplateEdit.path}
                element={ROUTES.adminOutreachTemplateEdit.element}
              />
              <Route
                path={ROUTES.adminCompliance.path}
                element={ROUTES.adminCompliance.element}
              />
            </Route>
          </Route>

          {/* --------AI CHAT------- */}
          <Route
            element={
              <ProtectedByFeatureFlag
                flagName={FLAGS.experimentalWebComponentsVisible}
                redirectPath={ROUTES.patients.path}
              />
            }
          >
            <Route path={ROUTES.aiChat.path} element={ROUTES.aiChat.element} />
          </Route>
        </Route>
      </Route>

      {/* --------SIGN IN------- */}
      <Route path={ROUTES.signin.path} element={ROUTES.signin.element} />
    </Routes>
  )
}

export default AppRoutes
